import type {
  LoginAuthenticationResponse,
  MedplumRequestOptions,
  PatchOperation,
} from "@medplum/core";
import { ContentType, createReference, encodeBase64 } from "@medplum/core";
import {
  AccessPolicy,
  Appointment,
  Basic,
  ExtractResource,
  HealthcareService,
  Location,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  Project,
  Resource,
  ResourceType,
  Schedule,
  Slot,
} from "@medplum/fhirtypes";
import { captureMessage } from "@sentry/react";
import axios from "axios";

import type {
  ClinicPractitionerRoleChange,
  ClinicPractitionerSearch,
  ClinicResourceCreateResult,
  ClinicResourceDeleteResult,
  ConfigResource,
  ConfigSchema,
  HealthcareServiceReferenceCounts,
  LocationReferenceCounts,
} from "@/types/clinic-configuration";
import type { Params } from "@/types/params";
import type { SchedulingParams } from "@/types/scheduling";
import type { MemberRole, TeamMember } from "@/types/users";
import {
  CLINIC_ACTIVE_FILTERS,
  HEALTHCARE_SERVICE_DEFAULT_APPOINTMENT_TYPE,
  HEALTHCARE_SERVICE_ID_PREFIX,
  LOCATION_ID_PREFIX,
  PRACTITIONER_ID_PREFIX,
} from "@/constants/clinic-configuration.constants";
import {
  FHIR_CODES,
  FHIR_EXTENSION_URLS,
  FHIR_EXTENSIONS,
  FHIR_IDENTIFIER_SYSTEMS,
} from "@/constants/fhir.constants";
import { medplumClient } from "@/lib/medplum";
import {
  clinicDetailsSchema,
  healthcareServiceSchema,
  locationSchema,
  practitionerSchema,
} from "@/lib/schemas";
import { parseMemberRole } from "@/lib/user-access";
import { formatHumanName, getExtensionValue } from "@/lib/utils";
import { isEditableField } from "@/lib/utils/clinic-configuration/configuration-schema-runtime";
import { getNextSequentialId } from "@/lib/utils/clinic-configuration/generate-entry-id";
import { buildKoraConfigPatchOperations } from "@/lib/utils/clinic-configuration/kora";
import {
  applyResourceDraft,
  buildResourcePatchOperations,
  buildVersionedPatchOperations,
} from "@/lib/utils/clinic-configuration/resource-patch";
import {
  getIdentifierValues,
  getReferenceId,
  getTelecomValue,
} from "@/lib/utils/helpers";
import {
  applyServiceLocations,
  buildPractitionerDeactivationWrites,
  buildPractitionerLinkWrites,
  getClinicPractitionerSchema,
  getPractitionerRemoval,
  toClinicPractitioners,
} from "@/lib/utils/helpers/clinic-configuration";
import { buildSchedulePatchOperations } from "@/lib/utils/helpers/scheduling";
import { isArray, isString } from "@/lib/utils/typeof";

/**
 * Every clinic in prod fits in one page of this size.
 */
const SEARCH_PAGE_SIZE = "1000";

/**
 * The filters several searches share. `system|` matches any identifier
 * value in that system: AKB writes one on everything it creates, which
 * keeps app members (also `Practitioner`s) out of a clinic's list.
 */
const toScopedSearch = ({
  projectId,
  identifierSystem,
}: Params.ScopedSearch): Record<string, string> => ({
  ...(projectId && { _project: projectId }),
  ...(identifierSystem && { identifier: `${identifierSystem}|` }),
});

/**
 * A clinic list, count or one-record read: the scoped search plus the
 * active filter of its type, so an inactive record is hidden from all of
 * them. Not in `toScopedSearch`: the Scheduling pages and the roles share
 * it, and they must still see inactive records.
 */
const toClinicSearch = (
  resourceType: ResourceType,
  scope: Params.ScopedSearch
): Record<string, string> => {
  /**
   * Widened, so a type with no filter (the clinic `Organization`) reads
   * none.
   */
  const filters: Partial<Record<ResourceType, Record<string, string>>> =
    CLINIC_ACTIVE_FILTERS;

  return { ...toScopedSearch(scope), ...filters[resourceType] };
};

/**
 * How many records match, without loading them.
 */
const countResources = async (
  resourceType: ResourceType,
  query: Record<string, string>
): Promise<number> => {
  const bundle = await medplumClient.search(resourceType, {
    ...query,
    _summary: "count",
  });

  return bundle.total ?? 0;
};

const EHR_SYNC_BASE_URL = import.meta.env.VITE_PUBLIC_EHR_SYNC_BASE_URL;

export const fetchPractitioner = async (
  practitionerId: string
): Promise<Practitioner> => {
  return medplumClient.readResource("Practitioner", practitionerId);
};

export const fetchPractitioners = async (
  params: Params.FetchPractitioners
): Promise<Practitioner[]> => {
  return searchAllResources("Practitioner", toScopedSearch(params));
};

/**
 * A search, not `readResource` (that is `fetchPractitioner`): the server
 * ignores `X-Medplum-Project` on a read, so only `_project` keeps an id
 * from another clinic out. `undefined` when there is no match.
 */
const findClinicResource = async <Type extends ResourceType>(
  resourceType: Type,
  { id, ...scope }: Params.FindClinicResource
): Promise<ExtractResource<Type> | undefined> => {
  return medplumClient.searchOne(resourceType, {
    ...toClinicSearch(resourceType, scope),
    _id: id,
  });
};

/**
 * A clinic's practitioners for the Practitioners page, or one of them by `id`,
 * with the member `Basic`s that point at them, in one search. Being a member
 * is not on the `Practitioner`: it is a `Basic` whose `subject` points at it.
 * `searchAllResources` returns the included `Basic`s in the same array, so
 * they are split out by type. `no-cache`, as in `fetchTeam`: member changes
 * go through custom operations that the client's search cache never sees.
 */
export const fetchClinicPractitioners = async ({
  projectId,
  id,
}: Params.FetchClinicPractitioners): Promise<ClinicPractitionerSearch> => {
  const resources: Resource[] = await searchAllResources(
    "Practitioner",
    {
      _project: projectId,
      /**
       * Not `id &&`: an empty id must still filter, or the one-record read
       * would become the whole clinic and return its first practitioner.
       */
      ...(id !== undefined && { _id: id }),
      ...CLINIC_ACTIVE_FILTERS.Practitioner,
      _revinclude: "Basic:subject",
      /**
       * Without a sort the server returns storage order, so a saved record
       * jumps to the end of the list. By name, a practitioner's duplicate
       * records also sit next to each other.
       */
      _sort: "family,given",
    },
    { cache: "no-cache" }
  );

  return {
    practitioners: resources.filter(
      (resource): resource is Practitioner =>
        resource.resourceType === "Practitioner"
    ),
    members: resources.filter(
      (resource): resource is Basic => resource.resourceType === "Basic"
    ),
  };
};

/**
 * A clinic record a write is about to change, read fresh with the filters
 * the drawer reads with: the AKB identifier and the active filter. A miss
 * throws: the record is gone, or it is not one this page edits. The message
 * goes to Sentry, so it names the record in full.
 */
const requireClinicResource = async <Type extends ResourceType>(
  resourceType: Type,
  params: Params.RequireClinicResource
): Promise<ExtractResource<Type>> => {
  const { projectId, id } = params;
  const stored = await findClinicResource(resourceType, params);

  if (!stored) {
    throw new Error(
      `No ${resourceType} "${id}" found in project "${projectId}".`
    );
  }

  return stored;
};

/**
 * One resource's patch in `patchResourcesInTransaction`.
 */
type TransactionPatch = {
  resourceType: ResourceType;
  id: string;
  operations: PatchOperation[];
};

/**
 * One conditional create in `patchResourcesInTransaction`: created only
 * when nothing matches `ifNoneExist`.
 */
type TransactionCreate = {
  resource: Resource;
  ifNoneExist: Record<string, string>;
};

/**
 * PATCHes, and conditional creates after them, that all apply or none do:
 * a failed `test` on any entry rolls back the whole bundle. Inside a
 * bundle, a JSON Patch travels as a base64 `Binary`. `executeBatch` does
 * not clear the client's search cache, as `patchResource` does, so this
 * clears it for each type. A server that runs the bundle as a batch, not
 * one atomic transaction, answers 200 with the failed entry inside, so each
 * entry's status is checked too. A create that matched answers 200 and
 * wrote nothing, which is a success: the record it would write exists. The
 * saved resources come back in the order of `patches`, `undefined` where a
 * successful entry has none: the server may answer without the body. The
 * creates' entries follow them.
 */
const patchResourcesInTransaction = async (
  patches: TransactionPatch[],
  creates: TransactionCreate[] = []
): Promise<(Resource | undefined)[]> => {
  const bundle = await medplumClient.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: [
      ...patches.map(({ resourceType, id, operations }) => ({
        resource: {
          resourceType: "Binary" as const,
          contentType: ContentType.JSON_PATCH,
          data: encodeBase64(JSON.stringify(operations)),
        },
        request: { method: "PATCH" as const, url: `${resourceType}/${id}` },
      })),
      ...creates.map(({ resource, ifNoneExist }) => ({
        resource,
        request: {
          method: "POST" as const,
          url: resource.resourceType,
          ifNoneExist: new URLSearchParams(ifNoneExist).toString(),
        },
      })),
    ],
  });
  new Set([
    ...patches.map(({ resourceType }) => resourceType),
    ...creates.map(({ resource }) => resource.resourceType),
  ]).forEach((resourceType) => medplumClient.invalidateSearches(resourceType));

  const entries = bundle.entry ?? [];
  const urls = [
    ...patches.map(({ resourceType, id }) => `${resourceType}/${id}`),
    ...creates.map(
      ({ resource, ifNoneExist }) =>
        `${resource.resourceType}?${new URLSearchParams(ifNoneExist).toString()}`
    ),
  ];

  if (entries.length !== urls.length) {
    throw new Error(
      `The transaction writing ${urls.join(", ")} failed: ${entries.length} entries in the response for ${urls.length} entries.`
    );
  }

  const failedIndex = entries.findIndex(
    ({ response }) => !response?.status.startsWith("2")
  );

  if (failedIndex !== -1) {
    throw new Error(
      `The transaction writing ${urls.join(", ")} failed at entry ${failedIndex} (${urls[failedIndex]}): status ${entries[failedIndex]?.response?.status}.`
    );
  }

  return entries.map(({ resource }) => resource);
};

/**
 * A new role as a conditional create, matched on its identifier in
 * this clinic, so a role another writer made since is not duplicated.
 */
const toRoleCreate = (
  role: PractitionerRole,
  projectId: string
): TransactionCreate => {
  const [identifier] = getIdentifierValues(
    [role],
    FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER_ROLE
  );

  return {
    resource: role,
    ifNoneExist: {
      _project: projectId,
      identifier: `${FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER_ROLE}|${identifier}`,
    },
  };
};

/**
 * Replaces the role where the fresh read found it. `changeMemberRole`
 * assumes `/extension/0`, which holds only for the invite bot's `Basic`s.
 * The index is safe for the same reason as in `buildResourcePatchOperations`:
 * the `versionId` test fails if the `Basic` changed since the user loaded it.
 */
const buildMemberRolePatchOperations = (
  member: Basic,
  { role, versionId }: ClinicPractitionerRoleChange
): PatchOperation[] => {
  const index =
    member.extension?.findIndex(
      ({ url }) => url === FHIR_EXTENSIONS.MEMBER_ROLE
    ) ?? -1;

  if (index === -1) {
    throw new Error(
      `Member Basic "${member.id}" has no member-role extension.`
    );
  }

  return [
    { op: "test", path: "/meta/versionId", value: versionId },
    { op: "replace", path: `/extension/${index}/valueCode`, value: role },
  ];
};

/**
 * Patches against a fresh read, so the patch is built from what is stored
 * now. `versionId` is still the one the user loaded: the `test` op must
 * fail if anything wrote the practitioner since. The read brings the
 * member `Basic`s too: an identifier alone cannot tell a member apart, and
 * a member's email is its login, which no save may write. The schema is
 * picked from this fresh read, like the drawer's from the list. When they
 * disagree (a practitioner became a member or gained a Scheduling identifier
 * since the list loaded), an edit to a field that is now read-only fails
 * the save rather than being dropped while the save reports success. A
 * member's role is on its `Basic`, and where the practitioner works and what
 * they offer is on their roles and `Schedule`s, so those are written with
 * the profile in one transaction. The messages go to Sentry, so they name
 * the record in full.
 */
export const updatePractitioner = async ({
  projectId,
  id,
  versionId,
  draft,
  dirtyFieldNames,
  roleChange,
  linkWrites = { roleCreates: [], rolePatches: [], schedulePatches: [] },
}: Params.UpdatePractitioner): Promise<Practitioner> => {
  const [record] = toClinicPractitioners(
    await fetchClinicPractitioners({ projectId, id })
  );

  if (!record) {
    throw new Error(`No Practitioner "${id}" found in project "${projectId}".`);
  }

  const schema = getClinicPractitionerSchema(record);
  const readonlyEdits = dirtyFieldNames.filter((name) => {
    const field: ConfigSchema.Field | undefined =
      schema.fields[name as keyof typeof schema.fields];
    return !!field && !isEditableField(field);
  });

  if (readonlyEdits.length > 0) {
    throw new Error(
      `Practitioner "${id}" in project "${projectId}" changed since it was loaded: ${readonlyEdits.join(", ")} can no longer be edited.`
    );
  }

  const operations = buildResourcePatchOperations(
    record.practitioner,
    schema,
    draft,
    dirtyFieldNames,
    versionId
  );

  const { roleCreates, rolePatches, schedulePatches } = linkWrites;
  const hasLinkWrites =
    roleCreates.length + rolePatches.length + schedulePatches.length > 0;

  if (!roleChange && !hasLinkWrites) {
    return medplumClient.patchResource("Practitioner", id, operations);
  }

  const { member } = record;
  const memberId = member?.id;

  if (roleChange && !memberId) {
    throw new Error(
      `Practitioner "${id}" in project "${projectId}" is no longer a member, so its role cannot be changed.`
    );
  }

  /**
   * The `Practitioner` goes first, and goes too when only the role or the
   * links changed: then its patch is only the `versionId` test, since the
   * drawer saves the one record the user loaded. A role create matches on
   * its identifier, so a role another writer made since is not duplicated.
   */
  const [saved] = await patchResourcesInTransaction(
    [
      { resourceType: "Practitioner", id, operations },
      ...(roleChange && member && memberId
        ? [
            {
              resourceType: "Basic" as const,
              id: memberId,
              operations: buildMemberRolePatchOperations(member, roleChange),
            },
          ]
        : []),
      ...rolePatches.map((patch) => ({
        resourceType: "PractitionerRole" as const,
        ...patch,
      })),
      ...schedulePatches.map((patch) => ({
        resourceType: "Schedule" as const,
        ...patch,
      })),
    ],
    roleCreates.map((role) => toRoleCreate(role, projectId))
  );

  /**
   * The save bar seeds the drawer's cache with the result, so a response
   * without the body is read again. `no-cache`: the client's cached read
   * holds the version this save replaced.
   */
  if (saved?.resourceType === "Practitioner") {
    return saved;
  }

  return medplumClient.readResource("Practitioner", id, { cache: "no-cache" });
};

/**
 * Sets an AKB-only practitioner inactive, with their active `Schedule`s, in one
 * transaction, so they change together. Nothing is deleted. The practitioner,
 * their roles and the clinic's `Schedule`s are read fresh, past the
 * client's cache: a role or `Schedule` AKB added since the drawer loaded
 * must go inactive too, or the practitioner would stay bookable there. Each
 * patch still tests its version, so a write between this read and the
 * transaction fails all of it. The practitioner's read brings
 * the member `Basic`s, as in `updatePractitioner`: a practitioner who became a
 * member since the list loaded is refused, since removing a member is the
 * Team page's. The messages go to Sentry, so they name the record in full.
 */
export const deactivatePractitioner = async ({
  projectId,
  id,
  versionId,
}: Params.DeactivatePractitioner): Promise<void> => {
  const [search, roles, schedules] = await Promise.all([
    fetchClinicPractitioners({ projectId, id }),
    searchAllResources(
      "PractitionerRole",
      { _project: projectId, practitioner: `Practitioner/${id}` },
      { cache: "no-cache" }
    ),
    searchAllResources(
      "Schedule",
      { _project: projectId },
      { cache: "no-cache" }
    ),
  ]);
  const [record] = toClinicPractitioners(search);

  if (!record) {
    throw new Error(`No Practitioner "${id}" found in project "${projectId}".`);
  }
  if (getPractitionerRemoval(record) !== "deactivate") {
    throw new Error(
      `Practitioner "${id}" in project "${projectId}" is no longer AKB-only (${record.sources.join(", ")}), so it cannot be deactivated.`
    );
  }

  const { practitionerOperations, schedulePatches } =
    buildPractitionerDeactivationWrites({
      practitioner: record.practitioner,
      versionId,
      roles,
      schedules,
    });

  await patchResourcesInTransaction([
    { resourceType: "Practitioner", id, operations: practitionerOperations },
    ...schedulePatches.map((patch) => ({
      resourceType: "Schedule" as const,
      ...patch,
    })),
  ]);
};

export const fetchLocations = async (
  params: Params.FetchLocations
): Promise<Location[]> => {
  return searchAllResources("Location", toClinicSearch("Location", params));
};

export const countLocations = async (
  params: Params.FetchLocations
): Promise<number> => {
  return countResources("Location", toClinicSearch("Location", params));
};

export const findLocation = async (
  params: Params.FindClinicResource
): Promise<Location | undefined> => {
  return findClinicResource("Location", params);
};

/**
 * Same as `updatePractitioner`: the patch is built from a fresh read and
 * guarded by the `versionId` the user loaded. The lookup keeps the AKB
 * identifier filter the drawer reads with, so a location AKB did not write
 * is never patched. The services offered here are stored on each service,
 * so their patches go with the location's in one transaction.
 */
export const updateLocation = async ({
  projectId,
  id,
  versionId,
  draft,
  dirtyFieldNames,
  servicePatches = [],
}: Params.UpdateLocation): Promise<Location> => {
  const stored = await requireClinicResource("Location", {
    projectId,
    id,
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.LOCATION,
  });

  const operations = buildResourcePatchOperations(
    stored,
    locationSchema,
    draft,
    dirtyFieldNames,
    versionId
  );

  if (servicePatches.length === 0) {
    return medplumClient.patchResource("Location", id, operations);
  }

  /**
   * The `Location` goes too when only services changed, as the `versionId`
   * test alone, as in `updatePractitioner`.
   */
  const [saved] = await patchResourcesInTransaction([
    { resourceType: "Location", id, operations },
    ...servicePatches.map((patch) => ({
      resourceType: "HealthcareService" as const,
      ...patch,
    })),
  ]);

  /**
   * As in `updatePractitioner`: the save bar seeds the drawer's cache with
   * the result.
   */
  if (saved?.resourceType === "Location") {
    return saved;
  }

  return medplumClient.readResource("Location", id, { cache: "no-cache" });
};

/**
 * Same as `updateLocation`. The lookup keeps the clinic details identifier
 * filter the page reads with, so only the clinic's own `Organization` is
 * ever patched.
 */
export const updateClinicDetails = async ({
  projectId,
  id,
  versionId,
  draft,
  dirtyFieldNames,
}: Params.UpdateClinicDetails): Promise<Organization> => {
  const stored = await requireClinicResource("Organization", {
    projectId,
    id,
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.CLINIC_DETAILS,
  });

  const operations = buildResourcePatchOperations(
    stored,
    clinicDetailsSchema,
    draft,
    dirtyFieldNames,
    versionId
  );

  return medplumClient.patchResource("Organization", id, operations);
};

/**
 * Every identifier value the clinic's records of `resourceType` hold in
 * `identifierSystem`, inactive ones included: a hidden record's ID is still
 * taken, so a create counting from the list alone would pick it again and
 * find it taken on every try. Identifiers only, since that is all a create
 * counts from.
 */
export const fetchClinicIdentifierValues = async ({
  projectId,
  resourceType,
  identifierSystem,
}: Params.FetchClinicIdentifierValues): Promise<string[]> => {
  const records = await searchAllResources(resourceType, {
    ...toScopedSearch({ projectId, identifierSystem }),
    _elements: "identifier",
  });

  return getIdentifierValues(records, identifierSystem);
};

/**
 * A FHIR conditional create, in a one-entry transaction because only the
 * entry's status says which way it went: `201` created it, `200` found a
 * match and created nothing. `createResourceIfNoneExist` returns the
 * resource either way. `undefined` means a match exists.
 */
const createResourceIfNoneExist = async <Type extends Resource>(
  resource: Type,
  query: Record<string, string>
): Promise<Type | undefined> => {
  const bundle = await medplumClient.executeBatch({
    resourceType: "Bundle",
    type: "transaction",
    entry: [
      {
        resource,
        request: {
          method: "POST",
          url: resource.resourceType,
          ifNoneExist: new URLSearchParams(query).toString(),
        },
      },
    ],
  });
  /**
   * `executeBatch` does not clear the client's search cache the way
   * `createResource` does. Without this, the refetches after a create get
   * cached results for up to a minute: the list misses the new record, and
   * the identifiers miss its ID, so the next create picks the same ID again.
   * A taken ID needs it as much — someone else's ID is what they miss.
   */
  medplumClient.invalidateSearches(resource.resourceType);
  const [entry] = bundle.entry ?? [];

  if (!entry?.response?.status.startsWith("201")) {
    return undefined;
  }
  if (!entry.resource) {
    throw new Error(
      `${resource.resourceType} was created, but the response has no resource.`
    );
  }

  return entry.resource as Type;
};

/**
 * Creates a record with the next ID of its identifier system. Medplum has
 * no counter, so the ID is the next number after the highest of
 * `identifiers` (`fetchClinicIdentifierValues`). If someone took it since
 * they loaded, the conditional create finds theirs and creates nothing; the
 * result says so, and the user tries again on fresh identifiers. One
 * attempt only: a retry with the next number would hide that they were out
 * of date.
 *
 * `meta.project` puts the record in the clinic's project. The server takes
 * the project of a create from the body only, and a super admin's token
 * would otherwise write it into the super admin project. The same goes for
 * `_project` in the match query: without it, a super admin's search would
 * find another clinic's `LOC001`.
 */
const createClinicResource = async <Type extends ConfigResource>({
  seed,
  schema,
  identifierSystem,
  idPrefix,
  projectId,
  identifiers,
  draft,
  dirtyFieldNames,
}: Params.CreateClinicResource<Type>): Promise<
  ClinicResourceCreateResult<Type>
> => {
  const identifier = getNextSequentialId(identifiers, { prefix: idPrefix });
  const resource = applyResourceDraft(
    {
      ...seed,
      meta: { project: projectId },
      identifier: [{ system: identifierSystem, value: identifier }],
    },
    schema,
    draft,
    dirtyFieldNames
  );

  const created = await createResourceIfNoneExist(resource, {
    _project: projectId,
    identifier: `${identifierSystem}|${identifier}`,
  });

  if (!created) {
    /**
     * A warning, not an error: nothing broke, but each one is a create the
     * user had to repeat. How often it happens says whether the
     * no-retry choice holds up.
     */
    captureMessage(`${seed.resourceType} create found its ID taken`, {
      level: "warning",
      extra: { projectId, identifier },
    });
    return { status: "taken", identifier };
  }

  return { status: "created", record: created };
};

/**
 * The clinic's own `Organization`, one per project, which AKB writes from
 * the clinic details sheet. `undefined` when AKB has not written it yet.
 */
export const findClinicDetailsOrganization = async (
  projectId: string
): Promise<Organization | undefined> => {
  return medplumClient.searchOne(
    "Organization",
    toScopedSearch({
      projectId,
      identifierSystem: FHIR_IDENTIFIER_SYSTEMS.CLINIC_DETAILS,
    })
  );
};

/**
 * A new location and service point at the clinic's `Organization`, as
 * AKB's do. Without it the create fails: a record that points at no clinic
 * is not one AKB would write.
 */
const requireClinicDetailsOrganization = async (
  projectId: string
): Promise<Organization> => {
  const organization = await findClinicDetailsOrganization(projectId);

  if (!organization) {
    throw new Error(`Project ${projectId} has no clinic details Organization.`);
  }

  return organization;
};

/**
 * `managingOrganization` is a reference by identifier, not by id, the same
 * as AKB writes it. The search matches the system alone, so an identifier
 * without a value can still be found; it would point at no clinic, so the
 * create fails instead.
 */
export const createLocation = async (
  params: Params.CreateLocation
): Promise<ClinicResourceCreateResult<Location>> => {
  const { projectId } = params;
  const organization = await requireClinicDetailsOrganization(projectId);
  const [clinicCodeName] = getIdentifierValues(
    [organization],
    FHIR_IDENTIFIER_SYSTEMS.CLINIC_DETAILS
  );

  if (!clinicCodeName?.trim()) {
    throw new Error(
      `Organization "${organization.id}" in project "${projectId}" has no clinic details identifier value.`
    );
  }

  return createClinicResource<Location>({
    seed: {
      resourceType: "Location",
      managingOrganization: {
        identifier: {
          system: FHIR_IDENTIFIER_SYSTEMS.CLINIC_DETAILS,
          value: clinicCodeName,
        },
      },
    },
    schema: locationSchema,
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.LOCATION,
    idPrefix: LOCATION_ID_PREFIX,
    ...params,
  });
};

/**
 * A new AKB practitioner, written as AKB writes one: active, in the clinic's
 * language, through `createClinicResource`, so a taken ID writes nothing.
 * The practitioner's roles are a second write, `createPractitionerRoles`, sent
 * only once this one created the record: a conditional create that matched
 * writes nothing, and roles in the same bundle would point at the practitioner
 * who holds the ID.
 */
export const createPractitioner = async (
  params: Params.CreatePractitioner
): Promise<ClinicResourceCreateResult<Practitioner>> => {
  const clinic = await requireClinicDetailsOrganization(params.projectId);

  return createClinicResource<Practitioner>({
    seed: {
      resourceType: "Practitioner",
      active: true,
      ...(clinic.language && { language: clinic.language }),
    },
    schema: practitionerSchema,
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.PRACTITIONER,
    idPrefix: PRACTITIONER_ID_PREFIX,
    ...params,
  });
};

/**
 * A new practitioner's roles, one per picked location, holding the picked
 * services, in one transaction. A failure leaves the practitioner with no
 * location: the practitioner's create cannot be undone, so the caller tells the
 * user to pick them again.
 */
export const createPractitionerRoles = async ({
  projectId,
  practitioner,
  locations,
  locationsChange,
  servicesChange,
}: Params.CreatePractitionerRoles): Promise<void> => {
  const clinic = await requireClinicDetailsOrganization(projectId);
  const { roleCreates } = buildPractitionerLinkWrites({
    projectId,
    practitioner,
    roles: [],
    schedules: [],
    locations,
    clinic,
    locationsChange,
    servicesChange,
  });

  if (roleCreates.length > 0) {
    await patchResourcesInTransaction(
      [],
      roleCreates.map((role) => toRoleCreate(role, projectId))
    );
  }
};

/**
 * How many records point at `target`, under the name `references` gives
 * each count. The counts are independent, so they are asked at once.
 */
const countClinicResourceReferences = async <Reference extends string>({
  projectId,
  target,
  references,
}: Params.CountClinicResourceReferences<Reference>): Promise<
  Record<Reference, number>
> => {
  /**
   * `Object.keys` and `Object.fromEntries` drop the key type; the keys are
   * `references`' own.
   */
  const names = Object.keys(references) as Reference[];
  const counts = await Promise.all(
    names.map((name) => {
      const { resourceType, param } = references[name];
      return countResources(resourceType, {
        _project: projectId,
        [param]: target,
      });
    })
  );

  return Object.fromEntries(
    names.map((name, index) => [name, counts[index]])
  ) as Record<Reference, number>;
};

/**
 * Deletes a record nothing points at. One that is still used is left as it
 * is, and the result says by how many. The counts are asked of the server
 * at the moment of the delete, never of cached lists: the server does not
 * check references on delete, so this is the only guard.
 *
 * The lookup goes first, so an id outside the clinic sends no counts.
 */
const deleteClinicResource = async <Reference extends string>({
  resourceType,
  references,
  ...lookup
}: Params.DeleteClinicResource<Reference>): Promise<
  ClinicResourceDeleteResult<Record<Reference, number>>
> => {
  const { projectId, id } = lookup;
  await requireClinicResource(resourceType, lookup);

  const counts = await countClinicResourceReferences({
    projectId,
    target: `${resourceType}/${id}`,
    references,
  });

  if (Object.values<number>(counts).some((count) => count > 0)) {
    /**
     * Info only: the guard worked. Reported so we can see how often users
     * try to delete a record that is still in use.
     */
    captureMessage(`${resourceType} delete refused: still in use`, {
      level: "info",
      extra: { projectId, id, ...counts },
    });
    return { status: "inUse", references: counts };
  }

  await medplumClient.deleteResource(resourceType, id);
  return { status: "deleted" };
};

/**
 * Deletes a location no service or practitioner role points at. Only a
 * location AKB wrote can be deleted.
 */
export const deleteLocation = async ({
  projectId,
  id,
}: Params.DeleteLocation): Promise<
  ClinicResourceDeleteResult<LocationReferenceCounts>
> => {
  return deleteClinicResource<keyof LocationReferenceCounts>({
    resourceType: "Location",
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.LOCATION,
    references: {
      services: { resourceType: "HealthcareService", param: "location" },
      practitionerRoles: {
        resourceType: "PractitionerRole",
        param: "location",
      },
    },
    projectId,
    id,
  });
};

export const fetchHealthcareServices = async (
  params: Params.FetchHealthcareServices
): Promise<HealthcareService[]> => {
  return searchAllResources(
    "HealthcareService",
    toClinicSearch("HealthcareService", params)
  );
};

export const countHealthcareServices = async (
  params: Params.FetchHealthcareServices
): Promise<number> => {
  return countResources(
    "HealthcareService",
    toClinicSearch("HealthcareService", params)
  );
};

export const findHealthcareService = async (
  params: Params.FindClinicResource
): Promise<HealthcareService | undefined> => {
  return findClinicResource("HealthcareService", params);
};

/**
 * Same as `updateLocation`: the patch is built from a fresh read and
 * guarded by the `versionId` the user loaded, and the lookup keeps the AKB
 * identifier filter. The practitioners who offer the service are stored on
 * their roles, so the role patches go with the service's in one transaction.
 */
export const updateHealthcareService = async ({
  projectId,
  id,
  versionId,
  draft,
  dirtyFieldNames,
  locationsChange,
  rolePatches = [],
}: Params.UpdateHealthcareService): Promise<HealthcareService> => {
  const stored = await requireClinicResource("HealthcareService", {
    projectId,
    id,
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.HEALTHCARE_SERVICE,
  });

  /**
   * The locations go on the same copy as the fields, so the save is one
   * patch: a second patch of the service would fail the `versionId` test
   * the first one moved.
   */
  const edited = applyResourceDraft(
    stored,
    healthcareServiceSchema,
    draft,
    dirtyFieldNames
  );
  const operations = buildVersionedPatchOperations(
    stored,
    locationsChange ? applyServiceLocations(edited, locationsChange) : edited,
    versionId
  );

  if (rolePatches.length === 0) {
    return medplumClient.patchResource("HealthcareService", id, operations);
  }

  /**
   * The service goes too when only its practitioners changed, as the
   * `versionId` test alone, as in `updatePractitioner`.
   */
  const [saved] = await patchResourcesInTransaction([
    { resourceType: "HealthcareService", id, operations },
    ...rolePatches.map((patch) => ({
      resourceType: "PractitionerRole" as const,
      ...patch,
    })),
  ]);

  /**
   * As in `updatePractitioner`: the save bar seeds the drawer's cache with
   * the result.
   */
  if (saved?.resourceType === "HealthcareService") {
    return saved;
  }

  return medplumClient.readResource("HealthcareService", id, {
    cache: "no-cache",
  });
};

/**
 * A new service gets the values AKB sets and the user never types, so Kora
 * reads it like an AKB one. `all-locations` and the list come from the
 * create drawer's locations card; the seed holds the flag where AKB writes
 * it, so the card's value replaces it in place. Without the flag Kora drops
 * the service. Auto-scheduling is off, so a person books it: Kora reads a
 * missing flag as allowed.
 */
export const createHealthcareService = async ({
  locationsChange,
  ...params
}: Params.CreateHealthcareService): Promise<
  ClinicResourceCreateResult<HealthcareService>
> => {
  const organization = await requireClinicDetailsOrganization(params.projectId);
  const { CODE, DISPLAY, DURATION_MINUTES } =
    HEALTHCARE_SERVICE_DEFAULT_APPOINTMENT_TYPE;
  const duration = { value: DURATION_MINUTES, unit: "min" };

  const seed: HealthcareService = {
    resourceType: "HealthcareService",
    active: true,
    appointmentRequired: true,
    providedBy: createReference(organization),
    type: [{ coding: [{ code: CODE, display: DISPLAY }] }],
    extension: [
      {
        url: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_APPOINTMENT_TYPE_CODE,
        valueString: CODE,
      },
      {
        url: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_AUTO_SCHEDULING_ALLOWED,
        valueBoolean: false,
      },
      {
        url: FHIR_EXTENSIONS.HEALTHCARE_SERVICE_ALL_LOCATIONS,
        valueBoolean: false,
      },
      {
        url: FHIR_EXTENSIONS.SCHEDULING_PARAMETERS,
        extension: [
          {
            url: FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.DURATION,
            valueDuration: duration,
          },
          {
            url: FHIR_EXTENSION_URLS.SCHEDULING_PARAMETERS.ALIGNMENT_INTERVAL,
            valueDuration: duration,
          },
        ],
      },
    ],
  };

  return createClinicResource<HealthcareService>({
    seed: applyServiceLocations(seed, locationsChange),
    schema: healthcareServiceSchema,
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.HEALTHCARE_SERVICE,
    idPrefix: HEALTHCARE_SERVICE_ID_PREFIX,
    ...params,
  });
};

/**
 * Deletes a service no practitioner role points at. Only a service AKB
 * wrote can be deleted.
 */
export const deleteHealthcareService = async ({
  projectId,
  id,
}: Params.DeleteHealthcareService): Promise<
  ClinicResourceDeleteResult<HealthcareServiceReferenceCounts>
> => {
  return deleteClinicResource<keyof HealthcareServiceReferenceCounts>({
    resourceType: "HealthcareService",
    identifierSystem: FHIR_IDENTIFIER_SYSTEMS.HEALTHCARE_SERVICE,
    references: {
      practitionerRoles: { resourceType: "PractitionerRole", param: "service" },
    },
    projectId,
    id,
  });
};

export const fetchPractitionerRoleById = async (
  practitionerRoleId: string
): Promise<PractitionerRole> => {
  return medplumClient.readResource("PractitionerRole", practitionerRoleId);
};

export const fetchPractitionerRoleWithLocation = async (
  practitionerRoleId: string
): Promise<{ role: PractitionerRole; location: Location | undefined }> => {
  const bundle = await medplumClient.search("PractitionerRole", {
    _id: practitionerRoleId,
    _include: "PractitionerRole:location",
  });

  let role: PractitionerRole | undefined;
  let location: Location | undefined;

  for (const entry of bundle.entry ?? []) {
    const resourceType = entry.resource?.resourceType;

    if (resourceType === "PractitionerRole") {
      role = entry.resource as PractitionerRole;
    } else if (resourceType === "Location") {
      location = entry.resource as unknown as Location;
    }
  }

  if (!role) {
    role = await medplumClient.readResource(
      "PractitionerRole",
      practitionerRoleId
    );
  }

  return { role, location };
};

export const fetchPatient = async (patientId: string): Promise<Patient> => {
  return medplumClient.readResource("Patient", patientId);
};

export const fetchPractitionerRole = async (
  practitionerId: string
): Promise<PractitionerRole | undefined> => {
  return medplumClient.searchOne("PractitionerRole", {
    practitioner: `Practitioner/${practitionerId}`,
  });
};

/**
 * Each role with its location. Loads every page; the `_include`d
 * locations come in the same pages as their roles.
 */
export const fetchPractitionerRoles = async ({
  practitionerId,
  ...scope
}: Params.FetchPractitionerRoles = {}): Promise<{
  practitionerRoles: PractitionerRole[];
  locations: Location[];
}> => {
  const resources: Resource[] = await searchAllResources("PractitionerRole", {
    ...toScopedSearch(scope),
    ...(practitionerId && { practitioner: `Practitioner/${practitionerId}` }),
    _include: "PractitionerRole:location",
  });

  const practitionerRoles: PractitionerRole[] = [];
  const locations: Location[] = [];

  for (const resource of resources) {
    if (resource.resourceType === "PractitionerRole") {
      practitionerRoles.push(resource);
    } else if (resource.resourceType === "Location") {
      locations.push(resource);
    }
  }

  return { practitionerRoles, locations };
};

/**
 * The clinic's `Schedule`s, active or not: a practitioner's location change
 * sets them with their role.
 */
export const fetchSchedules = async (
  params: Params.FetchSchedules
): Promise<Schedule[]> => {
  return searchAllResources("Schedule", toScopedSearch(params));
};

export const fetchPractitionerRoleSchedule = async (
  practitionerRoleId: string
): Promise<Schedule | undefined> => {
  return medplumClient.searchOne("Schedule", {
    actor: `PractitionerRole/${practitionerRoleId}`,
  });
};

export const fetchSlots = async (
  scheduleId: string,
  range: { start: Date; end: Date }
): Promise<Slot[]> => {
  const params = new URLSearchParams();

  params.append("schedule", `Schedule/${scheduleId}`);
  params.append("start", `ge${range.start.toISOString()}`);
  params.append("start", `le${range.end.toISOString()}`);
  params.append("_count", SEARCH_PAGE_SIZE);

  return medplumClient.searchResources("Slot", params) as Promise<Slot[]>;
};

export const createSlot = async ({
  scheduleId,
  status,
  start,
  end,
  comment,
}: Params.CreateSlot): Promise<Slot> => {
  return medplumClient.createResource<Slot>({
    resourceType: "Slot",
    schedule: { reference: `Schedule/${scheduleId}` },
    status,
    start,
    end,
    comment,
  });
};

export const deleteSlot = async ({
  slotId,
}: Params.DeleteSlot): Promise<void> => {
  await medplumClient.deleteResource("Slot", slotId);
};

export const fetchPractitionerAppointments = async (
  practitionerRoleId: string,
  range: { start: Date; end: Date }
): Promise<Appointment[]> => {
  const params = new URLSearchParams();
  params.append("actor", `PractitionerRole/${practitionerRoleId}`);
  params.append("date", `ge${range.start.toISOString()}`);
  params.append("date", `le${range.end.toISOString()}`);
  params.append("_count", SEARCH_PAGE_SIZE);
  return medplumClient.searchResources("Appointment", params) as Promise<
    Appointment[]
  >;
};

/**
 * `versionId` is the value the form was loaded from, not this function's
 * own fresh read — the `test` op it guards must compare against what the
 * user was editing against. The fresh read only locates the blocks.
 * `no-cache`: a cached read can be older than the loaded version, and the
 * test would then pass with ops built against the wrong block indexes.
 */
export const updateScheduleAvailability = async (
  scheduleId: string,
  params: SchedulingParams,
  versionId: string
): Promise<Schedule> => {
  const schedule = await medplumClient.readResource("Schedule", scheduleId, {
    cache: "no-cache",
  });

  const operations = buildSchedulePatchOperations(schedule, params, versionId);

  return medplumClient.patchResource("Schedule", scheduleId, operations);
};

export const fetchEhrConnections = async (): Promise<Basic[]> => {
  const basics = await medplumClient.searchResources("Basic", {
    code: FHIR_CODES.EHR_CONNECTION,
    _count: SEARCH_PAGE_SIZE,
  });

  if (!isArray(basics)) {
    return [];
  }

  return basics;
};

export const fetchProject = async (projectId: string): Promise<Project> => {
  return medplumClient.readResource("Project", projectId);
};

export const fetchKoraConfig = async ({
  projectId,
}: Params.FetchKoraConfig): Promise<Basic | undefined> => {
  return medplumClient.searchOne("Basic", {
    _project: projectId,
    code: `${FHIR_CODES.KORA_CONFIG_SYSTEM}|${FHIR_CODES.KORA_CONFIG}`,
  });
};

/**
 * `versionId` is the value known to the caller from page load, not
 * necessarily this function's own fresh read — the `test` op it guards must
 * compare against what the user was editing against. See
 * `buildKoraConfigPatchOperations`.
 */
export const updateKoraConfig = async ({
  projectId,
  versionId,
  updates,
}: Params.UpdateKoraConfig): Promise<Basic> => {
  const basic = await fetchKoraConfig({ projectId });

  if (!basic) {
    throw new Error(`No Kora config found for project "${projectId}".`);
  }

  const operations = buildKoraConfigPatchOperations(basic, updates, versionId);

  return medplumClient.patchResource("Basic", basic.id!, operations);
};

/**
 * Every match, following Medplum's `next` link to the end. A second page
 * means a clinic outgrew what the lists were sized for, so it is reported
 * to Sentry — and still loaded, since a list that drops records looks
 * complete and is not.
 */
export const searchAllResources = async <Type extends ResourceType>(
  resourceType: Type,
  query: Record<string, string>,
  /**
   * A rest tuple, so a call without options passes none rather than
   * `undefined`.
   */
  ...options: [MedplumRequestOptions?]
) => {
  const pages = [];

  for await (const page of medplumClient.searchResourcePages(
    resourceType,
    { ...query, _count: SEARCH_PAGE_SIZE },
    ...options
  )) {
    pages.push(page);
  }

  if (pages.length > 1) {
    captureMessage(`${resourceType} search loaded ${pages.length} pages`, {
      level: "warning",
      extra: query,
    });
  }

  return pages.flat();
};

/**
 * Super-admin only — returns all projects, not scoped to the current user's membership.
 */
export const fetchProjects = async (): Promise<Project[]> => {
  const projects = await medplumClient.searchResources("Project", {
    _count: SEARCH_PAGE_SIZE,
    _sort: "name",
  });

  if (!isArray(projects)) {
    return [];
  }

  /**
   * Medplum sets meta.author.reference = "system" on internally created projects.
   * These are infrastructure-level and should not be exposed as selectable clinic projects.
   * Projects with superAdmin: true are the Medplum super-admin project and must also be hidden.
   */
  return projects.filter(
    (project) =>
      project.meta?.author?.reference !== "system" && !project.superAdmin
  );
};

export const updateProjectName = async ({
  projectId,
  name,
}: Params.UpdateProjectName): Promise<Project> => {
  return medplumClient.patchResource("Project", projectId, [
    { op: "replace", path: "/name", value: name },
  ]);
};

export const fetchMember = async (
  practitionerId: string
): Promise<Basic | undefined> => {
  const members = await medplumClient.searchResources("Basic", {
    code: FHIR_CODES.MEMBER,
  });

  return members.find(
    (basic) =>
      getExtensionValue<{ reference: string }>(
        basic,
        FHIR_EXTENSIONS.MEMBER_PROFILE
      )?.reference === `Practitioner/${practitionerId}`
  );
};

export const fetchAccessPolicyByRole = async (
  role: MemberRole
): Promise<AccessPolicy | undefined> => {
  const policies = await medplumClient.searchResources("AccessPolicy", {
    name: `role-${role}`,
  });

  return policies[0];
};

export const selectLoginProfile = async (
  login: string,
  profileId: string
): Promise<LoginAuthenticationResponse> => {
  return medplumClient.post<LoginAuthenticationResponse>("auth/profile", {
    login,
    profile: profileId,
  });
};

export const changeMemberRole = async ({
  memberId,
  role,
}: Params.ChangeMemberRole): Promise<Basic> => {
  return medplumClient.patchResource("Basic", memberId, [
    { op: "replace", path: "/extension/0/valueCode", value: role },
  ]);
};

export const removeMember = async ({
  memberId,
}: Params.RemoveMember): Promise<void> => {
  await medplumClient.deleteResource("Basic", memberId);
};

export const inviteMember = async ({
  firstName,
  lastName,
  email,
  role,
  projectId,
  upsert,
  sendEmail,
  locale,
}: Params.InviteMember): Promise<Basic> => {
  const accessToken = medplumClient.getAccessToken();

  const { data } = await axios.post<Basic>(
    `${EHR_SYNC_BASE_URL}/v1/admin/invite`,
    {
      first_name: firstName,
      last_name: lastName,
      email,
      role,
      project_id: projectId,
      upsert: upsert ?? true,
      send_email: sendEmail ?? true,
      email_template_params: { locale },
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  return data;
};

export const resetPassword = async ({
  email,
  locale,
}: Params.ResetPassword): Promise<void> => {
  await axios.post(`${EHR_SYNC_BASE_URL}/v1/admin/reset_password`, {
    email,
    email_template_params: { locale },
  });
};

export const setPassword = async ({
  id,
  secret,
  password,
}: Params.SetPassword): Promise<void> => {
  await medplumClient.post("auth/setpassword", { id, secret, password });
};

export const changePassword = async ({
  oldPassword,
  newPassword,
}: Params.ChangePassword): Promise<void> => {
  await medplumClient.post("auth/changepassword", { oldPassword, newPassword });
};

/**
 * `no-cache` is required on both searches: member mutations go through a custom
 * FHIR operation (`$konko-ai-invite-member`, `$konko-ai-remove-member`, etc.),
 * so MedplumClient never sees a standard create/update and never invalidates its
 * search cache. Without this, the list stays stale after invite/remove/role change.
 */
export const fetchTeam = async ({
  projectId,
}: Params.FetchTeam): Promise<TeamMember[]> => {
  const memberBasics = await medplumClient.searchResources(
    "Basic",
    {
      ...(projectId ? { _project: projectId } : {}),
      code: FHIR_CODES.MEMBER,
    },
    { cache: "no-cache" }
  );

  const practitionerIds = memberBasics
    .map((basic) =>
      getReferenceId(
        getExtensionValue<{ reference: string }>(
          basic,
          FHIR_EXTENSIONS.MEMBER_PROFILE
        )?.reference,
        "Practitioner"
      )
    )
    .filter(isString);

  const fetchedPractitioners =
    practitionerIds.length > 0
      ? await medplumClient.searchResources(
          "Practitioner",
          { _id: practitionerIds.join(",") },
          { cache: "no-cache" }
        )
      : [];

  const practitionerMap = new Map<string, Practitioner>(
    fetchedPractitioners
      .filter(({ id }) => !!id)
      .map(({ id, ...practitioner }) => [id, practitioner])
  );

  return memberBasics.map((basic) => {
    const practitionerId =
      getReferenceId(
        getExtensionValue<{ reference: string }>(
          basic,
          FHIR_EXTENSIONS.MEMBER_PROFILE
        )?.reference,
        "Practitioner"
      ) ?? "";
    const practitioner = practitionerId
      ? practitionerMap.get(practitionerId)
      : undefined;
    const deleted = !!practitionerId && !practitioner;
    const fullName = formatHumanName(practitioner);
    const email = getTelecomValue(practitioner?.telecom, "email") ?? "";
    const role = parseMemberRole(
      getExtensionValue<string>(basic, FHIR_EXTENSIONS.MEMBER_ROLE)
    );
    const memberId = role ? (basic.id ?? "") : null;

    return {
      practitionerId,
      memberId,
      fullName,
      email,
      role: role ?? null,
      deleted,
    };
  });
};
